import React, { FC, ReactNode } from "react";
import classNames from "classnames";
import { scoreCls } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { menuIcon } from "../../../../../components/MenuOptions/OverflowMenu";
import { ScoreTrack } from "../../../videos/selectedVideoCardV2/performance/ScoreTrack";
import { bandWord } from "../../../videos/selectedVideoCardV2/performance/pageScore";

/**
 * Product-page primitives: micro-label, score chips, subscore, the button class
 * map, and the glyphs the video page's panel kit doesn't have.
 *
 * Usage:
 *   <Micro>Overall</Micro> <BandChip score={72} /> <DeltaChip change={0} showZero />
 *   <SubScore label="Human conversion" question="Will a person buy from this page?" score={72} delta={4} />
 *   <button type="button" className={btnClass("tint", { disabled })} disabled={disabled}>Score now</button>
 *   <span aria-hidden="true">{glyph("lock", 16, 1.8)}</span>
 *
 * COPIED, NOT EXTRACTED, from PageScorePanel.tsx's private parts (:55-193). That
 * file stays as it is (D8): the product versions differ on purpose (--text-2
 * micro-labels, a neutral "0" delta). Classes are the live ones,
 * `font-CircularStdBlack` included, which renders Book (D2).
 */

// The live micro-label (VID/Header.tsx:62-63), but in --text-2 rather than
// --muted: a product-local contrast choice. The video page keeps text-muted.
const MICRO = "font-CircularStdBlack uppercase text-text-2";
export const MICRO_STYLE = { fontSize: 9.5, letterSpacing: ".12em" } as const;

export const Micro: FC<{ children: ReactNode; className?: string; id?: string }> = ({ children, className, id }) => (
  <div id={id} className={classNames(MICRO, className)} style={MICRO_STYLE}>
    {children}
  </div>
);

/** h24 pill, band colour on its own 8% tint — the word the colour finally gets. */
export const BandChip: FC<{ score: number | null }> = ({ score }) => (
  <span
    className={classNames("inline-flex items-center h-6 px-2.5 rounded-full font-CircularStdBlack shrink-0", scoreCls(score))}
    style={{ fontSize: 12, background: "color-mix(in srgb, currentColor 8%, transparent)" }}
  >
    {bandWord(score)}
  </span>
);

/**
 * ▲ 6 / ▼ 3 on --up / --down — direction and level are different questions, so
 * never band colours. `showZero` turns an equal score into a neutral "0" chip;
 * without it a zero renders nothing, as on the video page.
 */
export const DeltaChip: FC<{ change: number; small?: boolean; showZero?: boolean }> = ({ change, small, showZero }) => {
  if (change === 0 && !showZero) return null;
  const shape = classNames(
    "inline-flex items-center gap-1 rounded-full font-CircularStdBlack tabular-nums shrink-0",
    small ? "h-5 px-1.5" : "h-6 px-2",
  );
  const fontSize = small ? 10.5 : 11;
  if (change === 0) {
    return (
      <span className={classNames(shape, "bg-surface-2 text-text-2")} style={{ fontSize }}>
        <span className="sr-only">{"No change "}</span>0
      </span>
    );
  }
  const up = change > 0;
  return (
    <span className={classNames(shape, up ? "bg-up-bg text-up" : "bg-down-bg text-down")} style={{ fontSize }}>
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" style={{ transform: up ? "none" : "rotate(180deg)" }}>
        <polygon points="4,1 7.5,7 0.5,7" style={{ fill: "currentColor" }} />
      </svg>
      <span className="sr-only">{up ? "Up " : "Down "}</span>
      {Math.abs(change)}
    </span>
  );
};

interface SubScoreProps {
  /** The micro-label heading, at every width. */
  label: string;
  /** The question under the track, at every width. */
  question: string;
  score: number | null;
  /** Change from the previous score. null = nothing to compare; 0 = the neutral chip. */
  delta?: number | null;
  /** A left rule from sm, for the second column of a two-up grid. */
  divided?: boolean;
}

/**
 * A subscore: micro-label, value, band word, delta, track, question. The live
 * non-nested SubScore (PageScorePanel.tsx:163-192); the product page is never
 * a nested host, so the ledger branch is not copied.
 *
 * One product-local change: the live copy drops the micro-label, the band
 * word and the question below sm ("80 Sentiment"), which leaves the band to
 * colour alone on a phone. Here all three show at every width, as 09a, 09b
 * and 09c draw them ("SENTIMENT", "80 Strong", track, question).
 */
export const SubScore: FC<SubScoreProps> = ({ label, question, score, delta = null, divided }) => (
  <div className={classNames("min-w-0", divided && "sm:border-l sm:border-border-token sm:pl-5")}>
    <Micro>{label}</Micro>
    <span className="flex items-baseline gap-2 flex-wrap mt-2">
      <span
        className={classNames(
          "font-CircularStdBlack tabular-nums leading-none text-[18px] sm:text-[22px]",
          scoreCls(score),
        )}
      >
        {score == null ? "—" : score}
      </span>
      <span className={scoreCls(score)} style={{ fontSize: 12 }}>
        {bandWord(score)}
      </span>
      {delta != null && <DeltaChip change={delta} small showZero />}
    </span>
    {score != null && (
      <span className={classNames("block", scoreCls(score))} style={{ marginTop: 8 }}>
        <ScoreTrack value={score} size="sub" />
      </span>
    )}
    <div className="text-text-2" style={{ fontSize: 12, marginTop: 8 }}>
      {question}
    </div>
  </div>
);

/**
 * A focus ring for the order-form register's fields (CustomFieldText,
 * CustomSelectField), put on a product-local wrapper around them. The shared
 * fields draw no focus state: the text field sets `border: none` and
 * `outline: none` on focus, the select `outline: none !important`
 * (components/fields/CustomFieldText, CustomFieldSelect), and a passed `sx`
 * can't win because CustomFieldText spreads props before its own sx. So the
 * wrapper outranks them, with `!`: the ring is the page's own :focus-visible
 * ring (theme/tokens.ts: 2px --accent, 2px offset) on the field's 12px box —
 * the text field's TextField root, the select's input root (the one holding a
 * `.MuiSelect-select`; MUI 5.18 puts no MuiSelect-root class on it). The
 * shared SkuPdpLinkEditor's bare `input.field-input` gets the same ring on its
 * bordered wrapper (only a border tint otherwise); the editor stays untouched.
 *
 *   <div className={FIELD_FOCUS}> <CustomFieldText … /> <CustomSelectField … /> </div>
 */
export const FIELD_FOCUS = [
  "[&_.MuiTextField-root:focus-within]:!outline [&_.MuiTextField-root:focus-within]:!outline-2",
  "[&_.MuiTextField-root:focus-within]:!outline-offset-2 [&_.MuiTextField-root:focus-within]:!outline-accent",
  "[&_.MuiInputBase-root.Mui-focused:has(>.MuiSelect-select)]:!outline",
  "[&_.MuiInputBase-root.Mui-focused:has(>.MuiSelect-select)]:!outline-2",
  "[&_.MuiInputBase-root.Mui-focused:has(>.MuiSelect-select)]:!outline-offset-2",
  "[&_.MuiInputBase-root.Mui-focused:has(>.MuiSelect-select)]:!outline-accent",
  "[&_div:has(>input.field-input:focus-visible)]:!outline [&_div:has(>input.field-input:focus-visible)]:!outline-2",
  "[&_div:has(>input.field-input:focus-visible)]:!outline-offset-2",
  "[&_div:has(>input.field-input:focus-visible)]:!outline-accent",
].join(" ");

/* ---- Buttons --------------------------------------------------------------
 * Class-map copies of the live buttons, not imports, so each can take
 * `max-md:h-11` (44px below md). The inline `fontSize: 12.5` of the originals
 * is `text-[12.5px]` here. `whitespace-nowrap` is the handoff's. Disabled is
 * keyed on the option, so pass `disabled` to btnClass AND to the element.
 *
 *   primary    PrimaryButton (VID/performance/ScoreState.tsx:159-181): 36px,
 *              hover --accent-dark, the quiet --surface-2/--muted fill when disabled.
 *   onAlert    OutlineButton / RetryButton (:130-157), inside red strips; keeps
 *              its fixed h-8 above md. No hover change, as it renders live: the
 *              live `hover:border-accent` never shows, because the inline
 *              `borderColor: var(--track)` outranks it (:143-144).
 *   secondary  the Go back COLOURS (VID/PageStates.tsx:155-163): --surface-2,
 *              hover --track, at primary's row geometry (h-9, radius 10, 12.5px)
 *              so the two sit level in one row. Not the Go back button itself
 *              (h-10, rounded-xl, 13.5px): the page messages copy DetailNotFound's
 *              own Go back / Try again classes (PageStates.tsx:155-173), not this.
 *   tint       the handoff's "Soft": --tint / --accent-text, section start actions.
 *   ghost      transparent, hover --surface-2: disclosures, Cancel.
 *   text       row actions: underlined, hover --accent-text. `danger` = --down.
 * ------------------------------------------------------------------------ */

type BtnVariant = "primary" | "onAlert" | "secondary" | "tint" | "ghost" | "text";

const ROW =
  "inline-flex items-center justify-center gap-2 h-9 max-md:h-11 px-4 rounded-[10px] text-[12.5px] whitespace-nowrap transition-colors";
const QUIET = "bg-surface-2 text-muted cursor-not-allowed";

const BTN: Record<BtnVariant, { base: string; on: string; off: string }> = {
  primary: {
    base: `${ROW} font-CircularStdBlack`,
    on: "bg-accent text-white hover:bg-accent-dark",
    off: QUIET,
  },
  onAlert: {
    base: "inline-flex items-center justify-center gap-1.5 h-8 max-md:h-11 px-3.5 rounded-[10px] border border-track bg-surface text-text-1 text-[12.5px] whitespace-nowrap transition-colors",
    // No hover change, as the live button renders (see onAlert above).
    on: "",
    off: "opacity-60 cursor-not-allowed",
  },
  secondary: {
    base: ROW,
    on: "bg-surface-2 text-text-1 hover:bg-track",
    off: QUIET,
  },
  tint: {
    base: `${ROW} font-CircularStdBlack`,
    on: "bg-tint text-accent-text hover:bg-accent hover:text-white",
    off: QUIET,
  },
  ghost: {
    base: ROW,
    on: "bg-transparent text-text-1 hover:bg-surface-2",
    off: "bg-transparent text-muted cursor-not-allowed",
  },
  text: {
    base: "inline-flex items-center gap-1 max-md:min-h-11 text-[12.5px] underline underline-offset-[3px] whitespace-nowrap transition-colors",
    on: "text-text-1 hover:text-accent-text",
    off: "text-muted cursor-not-allowed",
  },
};

export const btnClass = (variant: BtnVariant, opts: { disabled?: boolean; danger?: boolean } = {}): string => {
  const v = BTN[variant];
  let state = v.on;
  if (opts.disabled) state = v.off;
  else if (opts.danger && variant === "text") state = "text-down";
  return classNames(v.base, state);
};

/* ---- Glyphs ---------------------------------------------------------------
 * The page's one glyph registry: only what VID/panels/icons.tsx and
 * OverflowMenu's MENU_ICONS lack, built with OverflowMenu's own `menuIcon`
 * (Lucide geometry, currentColor only), so a glyph drops into an
 * OverflowMenu entry at its 17px default. `menuIcon` sets
 * no aria-hidden: wrap a glyph in an aria-hidden span unless a label sits beside it.
 * ------------------------------------------------------------------------ */

const GLYPH_PATHS = {
  lock: '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  pause: '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>',
  message: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
  play: '<path d="M7 4.5v15l12-7.5z"/>',
  package:
    '<path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5M12 22V12"/>',
  spinner: '<path d="M21 12a9 9 0 1 1-6.2-8.6"/>',
} as const;

type GlyphName = keyof typeof GLYPH_PATHS;

/** size / strokeWidth default to menuIcon's 17 / 1.7; the panel kit's are 16 / 1.8. */
export const glyph = (name: GlyphName, size?: number, strokeWidth?: number) =>
  menuIcon(GLYPH_PATHS[name], size, strokeWidth);

/** The working strips' 16px spinner: an open arc in --accent-text on the portal's ppSpin. Still under reduced motion. */
export const Spinner: FC = () => (
  <span className="inline-flex shrink-0 text-accent-text animate-ppSpin motion-reduce:animate-none" aria-hidden="true">
    {glyph("spinner", 16, 2)}
  </span>
);
